/*
 * ------------------------------------------------------------
 * TEMPORARY: cooker launch PDP. Remove after 30 Nov 2026 (see snippets/pc-cooker-head.liquid).
 * Page-wide behaviour for the cooker page, loaded only there by snippets/pc-cooker-head.liquid.
 *
 * Header hides while scrolling down and comes back on scroll up. Focal has no such option (StoreHeader in
 * theme.js only handles sticky/transparent). While hidden, --enable-sticky-header is set to 0 on <html>, the
 * variable the theme already multiplies the header height by everywhere (getStickyHeaderOffset(), scroll
 * margins, sticky offsets), so everything pinned under the header moves up with it instead of leaving a gap.
 * Slide styles: .pc-header-hidden in assets/pc-cooker.css.
 *
 * Only scrolling the shopper does counts. Pages also move by themselves while nobody touches them (a phone's
 * address bar showing/hiding, bounce at the ends, the browser re-anchoring the page when something above
 * changes height); without the guards below that made the header come and go at intervals after scrolling stopped.
 * - INPUT_WINDOW: scroll counts only shortly after a touch, wheel, key or pointer input (covers momentum after a
 *   swipe and the smooth scrolls started by a click in the section nav or buy bar).
 * - SETTLE: right after the header slides, its own movement shifts sticky things on the page; ignore that.
 *
 * Text reveal: second block below.
 * ------------------------------------------------------------
 */
(() => {
  const MIN_DELTA = 8; // px of scroll in one direction before the header reacts, ignores jitter
  const INPUT_WINDOW = 1500; // ms after the shopper's last input during which scrolling is theirs
  const SETTLE = 400; // ms after the header slides (.3s in pc-cooker.css) during which scroll changes are ignored
  const root = document.documentElement;

  let lastY = window.scrollY, hidden = false, frame = 0, lastInput = 0, lastToggle = 0;

  const markInput = () => lastInput = performance.now();

  ["wheel", "touchstart", "touchmove", "touchend", "keydown", "pointerdown"].forEach((type) => {
    window.addEventListener(type, markInput, { passive: true, capture: true });
  });

  /* Dragging the scrollbar: one pointerdown, then moves with the button held */
  window.addEventListener("pointermove", (event) => event.buttons && markInput(), { passive: true, capture: true });

  const setHidden = (value) => {
    if (value === hidden) {
      return;
    }

    hidden = value;
    lastToggle = performance.now();
    root.classList.toggle("pc-header-hidden", value);

    if (value) {
      root.style.setProperty("--enable-sticky-header", "0");
    } else {
      root.style.removeProperty("--enable-sticky-header");
    }
  };

  const update = () => {
    frame = 0;

    const y = window.scrollY, delta = y - lastY, now = performance.now();
    const headerHeight = parseInt(getComputedStyle(root).getPropertyValue("--header-height")) || 0;

    if (y <= headerHeight) {
      setHidden(false); // always visible at the top of the page
    } else if (now - lastInput > INPUT_WINDOW || now - lastToggle < SETTLE) {
      lastY = y; // the page moved by itself: follow it without reacting, so the drift never adds up to a toggle
      return;
    } else if (Math.abs(delta) < MIN_DELTA) {
      return; // keep lastY until the movement is big enough
    } else {
      setHidden(delta > 0);
    }

    lastY = y;
  };

  window.addEventListener("scroll", () => {
    if (!frame) {
      frame = requestAnimationFrame(update);
    }
  }, { passive: true });
})();

/*
 * Text reveal on scroll. Each text (heading, paragraph, rich text, button) fades up once, the first time IT scrolls
 * into view, so text further down a tall section waits for the shopper instead of animating off screen. Texts that
 * come into view together play one after another, top to bottom. Same motion as the theme's article list
 * (theme.js ArticleList: opacity 0 > 1, 35px phones / 60px desktop, 600ms, staggered), built from the theme's own
 * CustomAnimation / ParallelEffect / CustomKeyframeEffect.
 *
 * Focal has no page-wide reveal: each section's custom element animates its own [reveal] markup, and none of the
 * sections on this page has one. Adding [reveal] to theme sections would mean editing shared files, so the text
 * is picked up here instead.
 *
 * - Texts on screen at load animate in on load (the observer reports them straight away).
 * - Cards in side-scrolling rows animate as they are swiped into view; closed FAQ answers when opened.
 * - Skipped: sections that move their own text or must always show (product info, parallax, buy bar, section nav,
 *   apps), anything inside drawers, popovers, menus and tab bars, and texts not displayed at all at load (e.g.
 *   the desktop-only copy on a phone), which could never come into view to be revealed.
 * - Only hidden by this script, never by CSS, so if it doesn't run every text simply shows.
 * theme.js loads after this file, so its helpers are only touched once the page has loaded.
 */
(() => {
  const SKIP_SECTIONS = ".shopify-section--main-product, .shopify-section--pc-parallax, .shopify-section--pc-buy-bar, .shopify-section--pc-section-nav, .shopify-section--apps";
  const TEXT = "h1, h2, h3, h4, h5, h6, p, .heading, .rte, .button";
  const SKIP_INSIDE = "drawer-content, popover-content, openable-element, tabs-nav, [reveal], [reveal-visibility]";
  const STAGGER = 90; // ms between two texts coming into view together
  const MAX_DELAY = 600; // ms, so a screenful of texts never keeps the last ones waiting long

  const reveal = (items) => {
    const reduceMotion = MediaFeatures.prefersReducedMotion();
    const distance = reduceMotion ? 0 : window.innerWidth < 1000 ? 35 : 60;

    new CustomAnimation(new ParallelEffect(items.map((item, index) => {
      return new CustomKeyframeEffect(item, {
        opacity: [0, 1],
        transform: [`translateY(${distance}px)`, "translateY(0)"]
      }, {
        duration: 600,
        delay: reduceMotion ? 0 : Math.min(STAGGER * index, MAX_DELAY),
        easing: "ease"
      });
    }))).play();
  };

  const setup = () => {
    const main = document.getElementById("main");

    if (!main || !("IntersectionObserver" in window) || typeof CustomAnimation === "undefined") {
      return;
    }

    /* Outermost texts only (a heading inside .rte animates with its .rte), and only ones displayed right now */
    const items = Array.from(main.querySelectorAll(TEXT)).filter((item) => {
      const section = item.closest(".shopify-section");

      return section && !section.matches(SKIP_SECTIONS) && !item.closest(SKIP_INSIDE) && !item.parentElement.closest(TEXT) && item.getClientRects().length > 0;
    });

    /* One callback can report several texts (a screenful at load, or a fast scroll): play them top to bottom */
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => entry.target)
        .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

      visible.forEach((item) => observer.unobserve(item));

      if (visible.length) {
        reveal(visible);
      }
    }, { rootMargin: "0px 0px -10% 0px" });

    items.forEach((item) => {
      item.style.opacity = "0";
      observer.observe(item);
    });
  };

  /*
   * Deferred scripts run in order once the HTML is parsed (readyState is already "interactive" here), and this file
   * runs before theme.js, so CustomAnimation doesn't exist yet. DOMContentLoaded fires after every deferred script,
   * theme.js included. "load" is a fallback in case this file ever runs after DOMContentLoaded.
   */
  let started = false;
  const start = () => {
    if (!started) {
      started = true;
      setup();
    }
  };

  if (document.readyState === "complete") {
    start();
  } else {
    document.addEventListener("DOMContentLoaded", start);
    window.addEventListener("load", start);
  }
})();

/*
 * Dummy pincode checker. The launch product ships 10 to 15 days after the order wherever it goes, so on this page
 * the pincode checker (the "sandeep_edd" block, <sandeep-edd> in assets/custom.js) skips the pincode data and answers
 * every 6-digit pincode with that range: "Order dispatched between Sat, 25 Oct and Thu, 30 Oct".
 *
 * Only SandeepEdd's check() is replaced, and only here (this file loads on the cooker page only): the field, the
 * "Check Availability" / "Change" button, the theme loader-button spinner and the result panels are the real ones.
 * Swapped on the class, not on each element, so the checker <product-rerender> re-creates on a variant change
 * gets it too. custom.js runs after this file, so the swap waits for DOMContentLoaded.
 */
(() => {
  const MIN_DAYS = 10;
  const MAX_DAYS = 15;
  const CHECK_DELAY = 700; // ms of "Checking availability…", so the answer doesn't look canned

  const format = (days) => {
    const date = new Date();

    date.setDate(date.getDate() + days);

    return date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  };

  const install = () => {
    const SandeepEdd = window.customElements.get("sandeep-edd");

    if (!SandeepEdd) {
      return;
    }

    SandeepEdd.prototype.check = function () {
      if (this.input.value.length !== 6 || this.dataset.state === "loading") {
        return;
      }

      this.setState("loading");

      setTimeout(() => {
        const result = this.querySelector('[data-edd-state="standard"]');

        result.innerHTML = `Order dispatched between <strong>${format(MIN_DAYS)}</strong> and <strong>${format(MAX_DAYS)}</strong>`;
        this.setState("standard");
      }, CHECK_DELAY);
    };
  };

  if (document.readyState === "complete") {
    install();
  } else {
    document.addEventListener("DOMContentLoaded", install);
  }
})();

/*
 * Card rows (sections/pc-features.liquid, sections/pc-story.liquid, sections/pc-videos.liquid): on desktop the theme only moves the row with
 * the arrows, so assets/pc-cooker.css lets it scroll natively there too (trackpad swipe, shift + wheel). The
 * theme's <multi-column> only updates the arrows' disabled state when they are clicked, so a swipe would leave them
 * stale; this follows the "scrollable-content:progress" event <scrollable-content> already sends while scrolling.
 */
(() => {
  document.addEventListener("scrollable-content:progress", (event) => {
    const arrows = event.target.closest(".pc-features")?.querySelector(".pc-features__arrows");

    if (!arrows) {
      return;
    }

    arrows.querySelector(".prev-next-button--prev").disabled = event.detail.progress < 1; // 1%: fractional scroll positions on zoomed or high-density screens
    arrows.querySelector(".prev-next-button--next").disabled = event.detail.progress > 99;
  });
})();

/*
 * Video cards (sections/pc-videos.liquid): each card is a toggle-button opening a theme <modal-content> that holds a
 * <native-video> or <external-video>. Those only play on a click on themselves, so this starts the video from the
 * card's own click, while the tap is still in progress: phone browsers only allow sound when playback starts inside
 * the tap, and the modal's "openable-element:open" event fires a moment too late for that. The first play() loads the
 * video from its <template>. Closing the popup (any way: x, Escape, overlay) pauses it via "openable-element:close".
 */
(() => {
  document.addEventListener("click", (event) => {
    const card = event.target.closest(".pc-videos__media[aria-controls]");

    document.getElementById(card?.getAttribute("aria-controls"))?.querySelector("native-video, external-video")?.play();
  });

  document.addEventListener("openable-element:close", (event) => {
    if (event.target.matches?.(".pc-videos__modal")) {
      event.target.querySelector("native-video, external-video")?.pause();
    }
  });
})();
